import { Link } from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { measureClick, measureExposure } from "@/lib/cta-measurement";

type Destination =
  | "/appointments"
  | "/contact"
  | "/resources/insurance-coverage"
  | "/resources/qualifications"
  | "/es/contacto"
  | "/es/cobertura-de-seguro"
  | "/es/requisitos-para-cirugia";

const ACTION_DESTINATIONS: Destination[] = ["/appointments", "/contact", "/es/contacto"];

/** A measurable exit from a completed service-page section.
 *  Scheduling actions use the red CTA pill; information pages use the blue one,
 *  matching the hero and category buttons elsewhere on the site. */
export function SectionCta({
  to,
  label,
  search,
  children,
}: {
  to: Destination;
  label: string;
  /** Optional typed search params, e.g. { topic: "insurance" } to prefill the contact form. */
  search?: { topic?: string };
  children: ReactNode;
}) {
  const isAction = ACTION_DESTINATIONS.includes(to);
  const linkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const element = linkRef.current;
    if (!element || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        measureExposure(label);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [label]);
  return (
    <aside className="mt-6 flex justify-center" aria-label={label.replaceAll("_", " ")}>
      <Button
        asChild
        className={`${isAction ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-brand-logo text-brand-foreground hover:opacity-90"} h-auto min-h-11 max-w-full rounded-full px-7 py-3 text-sm font-medium text-center whitespace-normal focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
      >
        <Link
          ref={linkRef}
          to={to}
          search={search}
          data-cta={label}
          onClick={() => {
            measureClick(label);
            trackEvent("in_page_cta_click", { cta_label: label, link_url: to, page_path: window.location.pathname });
          }}
        >
          {children}
        </Link>
      </Button>
    </aside>
  );
}